import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getUserRoles } from '../../../utils/userRoles';
import {
  fetchTeamPlayers,
  removeTeamPlayer,
  updateTeamPlayerStatus,
} from '../../../services/teamPlayersService';
import TableControls from '../../../components/common/TableControls';
import useTableControls from '../../../hooks/useTableControls';

const statusClassMap = {
  approved: 'success',
  pending: 'warning',
  rejected: 'danger',
};

const getSkillTags = (player) => {
  const tagSource = player?.playerProfile?.skillTags;

  if (Array.isArray(tagSource)) {
    return tagSource.map((tag) => tag?.trim()).filter(Boolean);
  }

  if (typeof tagSource === 'string') {
    return tagSource
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  }

  return [];
};

const TeamAdminPlayersPage = () => {
  const { token, user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [status, setStatus] = useState({ message: '', tone: 'info' });
  const [loadError, setLoadError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState('');

  const userRoles = getUserRoles(user);
  const isTeamAdmin = userRoles.has('department_admin');
  const isSuperAdmin = userRoles.has('super_admin');

  useEffect(() => {
    const loadPlayers = async () => {
      if (!token || (!isTeamAdmin && !isSuperAdmin)) return;

      setIsLoading(true);
      setLoadError('');

      try {
        const response = await fetchTeamPlayers(token);
        setPlayers(response.players || []);
      } catch (error) {
        setLoadError(error.message || 'Unable to load players.');
      } finally {
        setIsLoading(false);
      }
    };

    loadPlayers();
  }, [isSuperAdmin, isTeamAdmin, token]);

  const playerSorters = useMemo(
    () => ({
      createdAt: (first, second) => new Date(first.createdAt) - new Date(second.createdAt),
      name: (first, second) =>
        (first.playerProfile?.fullName || first.name || '').localeCompare(
          second.playerProfile?.fullName || second.name || '',
          undefined,
          { sensitivity: 'base' }
        ),
      email: (first, second) =>
        (first.email || '').localeCompare(second.email || '', undefined, { sensitivity: 'base' }),
      role: (first, second) =>
        (first.playerProfile?.primaryRole || '').localeCompare(
          second.playerProfile?.primaryRole || '',
          undefined,
          { sensitivity: 'base' }
        ),
      skills: (first, second) =>
        getSkillTags(first)
          .join(', ')
          .localeCompare(getSkillTags(second).join(', '), undefined, { sensitivity: 'base' }),
      status: (first, second) =>
        (first.approvalStatus || '').localeCompare(second.approvalStatus || '', undefined, {
          sensitivity: 'base',
        }),
    }),
    []
  );

  const {
    paginatedItems: visiblePlayers,
    totalItems,
    totalPages,
    page,
    pageSize,
    sortKey,
    sortDirection,
    setSortKey,
    setSortDirection,
    setPage,
    setPageSize,
    searchTerm,
    setSearchTerm,
    toggleDirection,
  } = useTableControls(players, {
    initialSortKey: 'createdAt',
    initialSortDirection: 'desc',
    sorters: playerSorters,
    searchKeys: [
      (player) => player.playerProfile?.fullName || player.name || '',
      'email',
      (player) => player.playerProfile?.primaryRole || '',
      (player) => getSkillTags(player).join(' '),
      'approvalStatus',
    ],
  });

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (!isTeamAdmin && !isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  const updatePlayerInState = (updatedPlayer) => {
    setPlayers((prev) => prev.map((player) => (player.id === updatedPlayer.id ? updatedPlayer : player)));
  };

  const handleUpdateStatus = async (playerId, action) => {
    setIsUpdating(playerId + action);
    setStatus({ message: '', tone: 'info' });

    try {
      const response = await updateTeamPlayerStatus(playerId, action, token);
      updatePlayerInState(response.player);
      setStatus({
        message: action === 'approve' ? 'Player approved successfully.' : 'Player rejected successfully.',
        tone: 'success',
      });
    } catch (error) {
      setStatus({ message: error.message || 'Unable to update player.', tone: 'danger' });
    } finally {
      setIsUpdating('');
    }
  };

  const handleRemove = async (playerId) => {
    setIsUpdating(playerId + 'remove');
    setStatus({ message: '', tone: 'info' });

    try {
      await removeTeamPlayer(playerId, token);
      setPlayers((prev) => prev.filter((player) => player.id !== playerId));
      setStatus({ message: 'Player removed from your team.', tone: 'success' });
    } catch (error) {
      setStatus({ message: error.message || 'Unable to remove player.', tone: 'danger' });
    } finally {
      setIsUpdating('');
    }
  };

  return (
    <div className="card shadow-sm border-0">
      <div className="card-body d-grid gap-4">
        <div className="d-flex flex-column flex-lg-row justify-content-lg-between gap-2">
          <div>
            <p className="eyebrow text-primary mb-1">Team players</p>
            <h1 className="h4 mb-2">Review and manage your players</h1>
            <p className="text-muted mb-0">
              Approve or reject player requests for your team and remove members who no longer belong under your management.
            </p>
          </div>
          <div className="d-flex align-items-center gap-2 text-muted">
            <span className="badge bg-primary-subtle text-primary-emphasis">Department admin tools</span>
          </div>
        </div>

        {isSuperAdmin && (
          <div className="alert alert-info py-2 mb-0">
            Viewing the department admin experience as a super admin. Actions may require a team assignment to succeed.
          </div>
        )}

        {status.message && (
          <div className={`alert alert-${status.tone === 'danger' ? 'danger' : 'success'} py-2 mb-0`}>
            {status.message}
          </div>
        )}

        <div className="card border-0 shadow-sm">
          <div className="card-body d-grid gap-3">
            <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-sm-between">
              <div>
                <p className="text-muted small mb-1">Players</p>
                <h2 className="h5 mb-0">Players awaiting your review</h2>
              </div>
              <span className="badge bg-secondary-subtle text-secondary-emphasis">Department admin view</span>
            </div>

            {loadError && <div className="alert alert-danger py-2 mb-0">{loadError}</div>}

            <TableControls
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSortKeyChange={setSortKey}
              onSortDirectionChange={(direction) =>
                direction ? setSortDirection(direction) : toggleDirection()
              }
              sortOptions={[
                { value: 'createdAt', label: 'Date added' },
                { value: 'name', label: 'Name' },
                { value: 'email', label: 'Email' },
                { value: 'role', label: 'Primary role' },
                { value: 'skills', label: 'Skills' },
                { value: 'status', label: 'Status' },
              ]}
              page={page}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              searchPlaceholder="Search players by name, email, or status"
            />

            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead>
                  <tr>
                    <th scope="col">Player</th>
                    <th scope="col">Email</th>
                    <th scope="col">Primary role</th>
                    <th scope="col">Skills</th>
                    <th scope="col">Status</th>
                    <th scope="col" className="text-end">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading && (
                    <tr>
                      <td colSpan="6" className="text-center text-muted">
                        Loading players...
                      </td>
                    </tr>
                  )}

                  {!isLoading && totalItems === 0 && (
                    <tr>
                      <td colSpan="6" className="text-center text-muted">
                        No players have been assigned to your team yet.
                      </td>
                    </tr>
                  )}

                  {visiblePlayers.map((player) => {
                    const displayName = player.playerProfile?.fullName || player.name || 'Unnamed player';
                    const primaryRole = player.playerProfile?.primaryRole || 'Not provided';
                    const badgeClass = statusClassMap[player.approvalStatus] || 'secondary';
                    const resolvedSkills = getSkillTags(player);

                    return (
                      <tr key={player.id}>
                        <td>
                          <div className="fw-semibold">{displayName}</div>
                          <div className="text-muted small">Joined {new Date(player.createdAt).toLocaleDateString()}</div>
                        </td>
                        <td>{player.email}</td>
                        <td>{primaryRole}</td>
                        <td>
                          {resolvedSkills.length ? (
                            <div className="d-flex flex-wrap gap-1">
                              {resolvedSkills.map((skill) => (
                                <span key={skill} className="badge text-bg-light text-capitalize">
                                  {skill}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted small">No skills selected</span>
                          )}
                        </td>
                        <td>
                          <span className={`badge text-bg-${badgeClass} text-capitalize`}>{player.approvalStatus}</span>
                        </td>
                        <td>
                          <div className="d-flex justify-content-end gap-2">
                            <button
                              type="button"
                              className="btn btn-outline-success btn-sm"
                              disabled={player.approvalStatus === 'approved' || isUpdating === player.id + 'approve'}
                              onClick={() => handleUpdateStatus(player.id, 'approve')}
                            >
                              {isUpdating === player.id + 'approve' ? 'Approving...' : 'Approve'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              disabled={player.approvalStatus === 'rejected' || isUpdating === player.id + 'reject'}
                              onClick={() => handleUpdateStatus(player.id, 'reject')}
                            >
                              {isUpdating === player.id + 'reject' ? 'Rejecting...' : 'Reject'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              disabled={isUpdating === player.id + 'remove'}
                              onClick={() => handleRemove(player.id)}
                            >
                              {isUpdating === player.id + 'remove' ? 'Removing...' : 'Remove'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamAdminPlayersPage;
